import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { AFRICA_COUNTRIES, SECTORS, HORIZONS, OBJECTIVES, CURRENCIES, RISK_PROFILES } from "@/lib/data";
import PageHeader from "@/components/PageHeader";
import { X, ArrowRight, CaretDown } from "@phosphor-icons/react";

export default function NewAssessment() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", countries: [], sector: "", sub_sector: "",
    budget_eur: 500000, currency: "USD", risk_profile: "Balanced",
    horizon: HORIZONS[1], objective: OBJECTIVES[0], notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [sectorOpen, setSectorOpen] = useState(false);
  const [horizonOpen, setHorizonOpen] = useState(false);
  const [objectiveOpen, setObjectiveOpen] = useState(false);

  const addCountry = (c) => {
    if (!form.countries.includes(c)) setForm((f) => ({ ...f, countries: [...f.countries, c] }));
    setCountryQuery("");
  };
  const removeCountry = (c) => setForm((f) => ({ ...f, countries: f.countries.filter((x) => x !== c) }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.countries.length === 0) return toast.error("Select at least one country");
    if (!form.title || !form.sector) return toast.error("Fill title and sector");
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessments", form);
      toast.success("Assessment created. Generating intelligence…");
      navigate(`/assessments/${data.id}`);
    } catch (err) {
      toast.error(extractApiError(err, "Failed to create"));
    } finally { setSubmitting(false); }
  };

  const filteredCountries = AFRICA_COUNTRIES.filter((c) =>
    c.toLowerCase().includes(countryQuery.toLowerCase()) && !form.countries.includes(c)
  ).slice(0, 10);

  const inputCls = "w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm sm:text-base";
  const selectBtnCls = "w-full flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 hover:border-primary/50 transition-colors text-sm sm:text-base";

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="new-assessment-page">
      <PageHeader eyebrow="New mandate" title="Brief an origination dossier" subtitle="Five fields. One mandate. Our research engine produces an institutional-grade dossier — market intelligence, regulatory brief, risk matrix, deal pipeline and execution blueprint." />

      <form onSubmit={submit} className="mt-8 sm:mt-10 space-y-6 sm:space-y-8">
        {/* Title */}
        <div>
          <label className="text-overline text-muted-foreground block mb-2.5">Mandate title</label>
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Côte d'Ivoire — Logistics Market Entry 2026" className={inputCls} data-testid="asm-title" required />
        </div>

        {/* Countries multi-select */}
        <div>
          <label className="text-overline text-muted-foreground block mb-2.5">Target countries</label>
          <div className="bg-card border border-border rounded-lg p-3 min-h-[3.25rem] flex flex-wrap gap-2 items-center">
            {form.countries.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 bg-teal-soft text-teal text-xs px-2.5 py-1 rounded-full font-medium" data-testid={`chip-${c}`}>
                {c} <button type="button" onClick={() => removeCountry(c)} aria-label={`Remove ${c}`}><X size={12} weight="bold" /></button>
              </span>
            ))}
            <input
              value={countryQuery}
              onFocus={() => setCountryOpen(true)}
              onBlur={() => setTimeout(() => setCountryOpen(false), 200)}
              onChange={(e) => { setCountryQuery(e.target.value); setCountryOpen(true); }}
              placeholder={form.countries.length === 0 ? "Search countries (e.g. Nigeria, Kenya)…" : "Add another…"}
              className="flex-1 min-w-[160px] bg-transparent outline-none text-sm"
              data-testid="asm-country-search"
            />
          </div>
          {countryOpen && filteredCountries.length > 0 && (
            <div className="mt-2 bg-card border border-border rounded-lg max-h-60 overflow-y-auto card-elev" data-testid="country-suggestions">
              {filteredCountries.map((c) => (
                <button key={c} type="button" onClick={() => addCountry(c)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary transition-colors" data-testid={`country-opt-${c}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sector */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="relative">
            <label className="text-overline text-muted-foreground block mb-2.5">Sector</label>
            <button type="button" onClick={() => setSectorOpen((v) => !v)} className={selectBtnCls} data-testid="asm-sector-toggle">
              <span className={form.sector ? "" : "text-muted-foreground"}>{form.sector || "Select sector"}</span>
              <CaretDown size={14} />
            </button>
            {sectorOpen && (
              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg max-h-72 overflow-y-auto card-elev" data-testid="sector-list">
                {SECTORS.map((s) => (
                  <button type="button" key={s} onClick={() => { setForm((f) => ({ ...f, sector: s })); setSectorOpen(false); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary transition-colors" data-testid={`sector-opt-${s}`}>{s}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-overline text-muted-foreground block mb-2.5">Sub-sector (optional)</label>
            <input value={form.sub_sector} onChange={(e) => setForm((f) => ({ ...f, sub_sector: e.target.value }))} placeholder="e.g. Cold chain, Last-mile" className={inputCls} data-testid="asm-subsector" />
          </div>
        </div>

        {/* Budget + Currency */}
        <div>
          <label className="text-overline text-muted-foreground block mb-2.5">Capital envelope</label>
          <div className="bg-card border border-border rounded-lg p-4 sm:p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                className="bg-secondary border border-border rounded-md px-3 py-2 text-sm font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="asm-currency"
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="data-large text-3xl sm:text-4xl text-primary">{form.budget_eur.toLocaleString()}</div>
            </div>
            <input
              type="range" min={50000} max={50000000} step={50000} value={form.budget_eur}
              onChange={(e) => setForm((f) => ({ ...f, budget_eur: Number(e.target.value) }))}
              className="w-full mt-3 accent-primary" data-testid="asm-budget"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2 font-mono">
              <span>50K</span><span>50M</span>
            </div>
          </div>
        </div>

        {/* Risk profile */}
        <div>
          <label className="text-overline text-muted-foreground block mb-2.5">Risk profile</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="asm-risk-profile">
            {RISK_PROFILES.map((rp) => (
              <button
                type="button"
                key={rp}
                onClick={() => setForm((f) => ({ ...f, risk_profile: rp }))}
                className={`border rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${form.risk_profile === rp ? "border-primary bg-teal-soft text-teal" : "border-border bg-card hover:border-primary/40"}`}
                data-testid={`risk-opt-${rp}`}
              >
                {rp}
              </button>
            ))}
          </div>
        </div>

        {/* Horizon + Objective */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="relative">
            <label className="text-overline text-muted-foreground block mb-2.5">Investment horizon</label>
            <button type="button" onClick={() => setHorizonOpen((v) => !v)} className={selectBtnCls} data-testid="asm-horizon-toggle">
              {form.horizon} <CaretDown size={14} />
            </button>
            {horizonOpen && (
              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg card-elev">
                {HORIZONS.map((h) => (
                  <button type="button" key={h} onClick={() => { setForm((f) => ({ ...f, horizon: h })); setHorizonOpen(false); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary transition-colors" data-testid={`horizon-opt-${h}`}>{h}</button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <label className="text-overline text-muted-foreground block mb-2.5">Strategic objective</label>
            <button type="button" onClick={() => setObjectiveOpen((v) => !v)} className={selectBtnCls} data-testid="asm-objective-toggle">
              {form.objective} <CaretDown size={14} />
            </button>
            {objectiveOpen && (
              <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-lg max-h-72 overflow-y-auto card-elev">
                {OBJECTIVES.map((o) => (
                  <button type="button" key={o} onClick={() => { setForm((f) => ({ ...f, objective: o })); setObjectiveOpen(false); }} className="w-full text-left px-3 py-2.5 text-sm hover:bg-secondary transition-colors" data-testid={`objective-opt-${o}`}>{o}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-overline text-muted-foreground block mb-2.5">Strategic notes (optional)</label>
          <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={4} placeholder="Any context the analysts should know — investment thesis, constraints, preferred partners…" className={inputCls} data-testid="asm-notes" />
        </div>

        <div className="pt-4 flex items-center justify-between flex-col sm:flex-row gap-4 border-t border-border">
          <div className="text-xs text-muted-foreground order-2 sm:order-1 leading-relaxed">
            ORIXA provides intelligence and decision-support information only. Not financial, legal, tax or investment advice.
          </div>
          <button disabled={submitting} type="submit" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3 font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 card-elev order-1 sm:order-2" data-testid="asm-submit">
            {submitting ? "Briefing…" : <>Generate dossier <ArrowRight weight="bold" /></>}
          </button>
        </div>
      </form>
    </div>
  );
}
